/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import { TouchableOpacity } from "react-native";
import Icon from "react-icons-kit";
import { closeRound } from "react-icons-kit/ionicons/closeRound";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { FadeInOutView, Text } from "UpduoLibrary";
import { skalePrimaryBlue, skaleBackgroundBlue } from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function AnnouncementModal() {
  const dispatch = useDispatch();

  const announcementObject = useSelector(
    (state: RootState) => state.announcementObject
  );

  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [doRender, setDoRender] = useState(true); // doRender needs to be initialized to true due to a React rendering quirk

  useEffect(() => {
    if (announcementObject?.heading || announcementObject?.content) {
      setHeading(announcementObject?.heading || "");
      setContent(announcementObject?.content || "");
      setIsVisible(true);
    } else {
      // When announcement object is cleared by _handleDismissAnnouncement,
      // hide modal (but without clearing local heading/content states, for smoothness)
      setIsVisible(false);
    }
  }, [announcementObject]);

  useEffect(() => {
    if (isVisible) {
      setDoRender(true);
    } else {
      // Let fade-out animation complete before unrendering component
      // (necessary for not blocking mouse clicks in area where the notification would reside)
      setTimeout(() => {
        setDoRender(false);
      }, 400);
    }
  }, [isVisible]);

  const _handleDismissAnnouncement = useCallback(() => {
    UpduoAnalytics.logEvent("user-announcement-dismiss", {
      heading: announcementObject?.heading,
      content: announcementObject?.content,
    });
    dispatch(ActionCreators.setAnnouncementObject({}));
  }, [announcementObject]);

  if (doRender) {
    return (
      <FadeInOutView
        style={{
          position: "fixed" as any, // ViewStyle doesn't expect position: "fixed" (but it works on web)
          bottom: 20,
          left: 40,
          borderRadius: 12,
          backgroundColor: skaleBackgroundBlue,
          padding: 20,
          minWidth: 200,
          maxWidth: 450,
        }}
        visible={isVisible}
      >
        <Text
          isHeading={true}
          style={{
            color: skalePrimaryBlue,
            fontSize: 20,
            marginBottom: 5,
            maxWidth: 400,
          }}
        >
          {heading}
        </Text>
        <Text style={{ color: skalePrimaryBlue, fontSize: 16 }}>{content}</Text>
        <TouchableOpacity
          onPress={_handleDismissAnnouncement}
          style={{ position: "absolute", top: 15, right: 15 }}
        >
          <Text style={{ color: skalePrimaryBlue }}>
            <Icon size={17} icon={closeRound} />
          </Text>
        </TouchableOpacity>
      </FadeInOutView>
    );
  }
  return null;
}
